import type { SketchPaletteWidthOption } from "@/types/stage-types.js";

export interface WidthSelectorConfig {
  readonly options: readonly SketchPaletteWidthOption[];
  readonly initialWidth?: number;
  readonly onSelect?: (option: SketchPaletteWidthOption) => void;
}

export interface WidthSelectorAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelectedWidth: (width: number) => void;
  readonly destroy: () => void;
}

export const createWidthSelector = (
  container: HTMLElement,
  config: WidthSelectorConfig
): WidthSelectorAPI => {
  const root = document.createElement("div");
  root.className = "sketch-tool-palette__widths";
  container.appendChild(root);

  let isDestroyed = false;
  let selectedWidth: number | null = null;
  const buttons = new Map<number, HTMLButtonElement>();

  const setSelectedWidth = (width: number): void => {
    selectedWidth = width;
    buttons.forEach((button, value) => {
      const isSelected = value === width;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", String(isSelected));
    });
  };

  const handleSelect = (option: SketchPaletteWidthOption): void => {
    if (isDestroyed) return;
    setSelectedWidth(option.value);
    config.onSelect?.(option);
  };

  const createWidthButton = (
    option: SketchPaletteWidthOption
  ): HTMLButtonElement => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "sketch-tool-palette__width";
    button.dataset.width = String(option.value);
    button.dataset.widthId = option.id;
    button.title = option.label ?? `${option.value}px`;
    button.setAttribute("aria-label", option.label ?? `${option.value}px`);

    const indicator = document.createElement("span");
    indicator.className = "sketch-tool-palette__width-indicator";
    indicator.style.setProperty(
      "--sketch-tool-palette-width",
      `${Math.max(1, option.value)}px`
    );
    button.appendChild(indicator);

    button.addEventListener("click", () => handleSelect(option));
    return button;
  };

  for (const option of config.options) {
    const button = createWidthButton(option);
    buttons.set(option.value, button);
    root.appendChild(button);
  }

  const fallbackWidth =
    typeof config.initialWidth === "number" && buttons.has(config.initialWidth)
      ? config.initialWidth
      : config.options[0]?.value;

  if (typeof fallbackWidth === "number") {
    setSelectedWidth(fallbackWidth);
  }

  const getElement = (): HTMLElement => root;

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    buttons.clear();
    root.innerHTML = "";
    if (root.parentElement === container) {
      container.removeChild(root);
    }
  };

  return {
    getElement,
    setSelectedWidth,
    destroy,
  };
};
